import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import CardHeaderSpan from "../../../Utils/CommonComponents/CardHeaderSpan";
import { Href } from "../../../Utils/Constants";
import { toast } from "react-toastify";

type SalesAgreementStatus =
  | "Pending Level 1"
  | "Pending Level 2"
  | "Pending Level 3"
  | "Approved"
  | "Rejected";
type ReviewAction = "approve" | "reject";

type ApprovalLevel = 1 | 2 | 3;

type ApprovalTrailEntry = {
  level: ApprovalLevel;
  action: "approved" | "rejected";
  by: string;
  remark: string;
  at: string;
};

type SalesAgreementRecord = {
  id: string;
  requestTitle: string;
  speakerDoctor: string;
  targetDoctor: string;
  requestDate: string;
  territory: string;
  notes: string;
  submittedBy: string;
  status: SalesAgreementStatus;
  currentLevel: ApprovalLevel;
  approvalTrail: ApprovalTrailEntry[];
  expenseDetails: ExpenseDetail[];
  attachments: AttachmentDetail[];
  totalExpense: number;
  reviewRemark?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
};

type SalesAgreementForm = {
  requestTitle: string;
  speakerDoctor: string;
  targetDoctor: string;
  requestDate: string;
  territory: string;
  notes: string;
};

type ExpenseDetail = {
  id: string;
  expenseType: string;
  expenseDate: string;
  amount: string;
  remarks: string;
};

type AttachmentDetail = {
  id: string;
  name: string;
  type: string;
  size: number;
  dataUrl: string;
};

const STORAGE_KEY = "sales-agreement-records-v1";

const doctorOptions = [
  { id: "dr-001", name: "Dr. Anjali Sharma", specialty: "Medicine" },
  { id: "dr-002", name: "Dr. Rajesh Mehta", specialty: "Diabetology" },
  { id: "dr-003", name: "Dr. Priya Nair", specialty: "Cardiology" },
  { id: "dr-004", name: "Dr. Vivek Iyer", specialty: "Orthopedics" },
  { id: "dr-005", name: "Dr. Sana Khan", specialty: "General Physician" },
  { id: "dr-006", name: "Dr. Nitin Verma", specialty: "Pediatrics" },
];

const territoryOptions = [
  "North HQ",
  "South HQ",
  "East HQ",
  "West HQ",
  "Central HQ",
];

const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
const MAX_ATTACHMENT_COUNT = 10;

const approvalHierarchy: {
  level: ApprovalLevel;
  label: string;
  approverRole: string;
}[] = [
  { level: 1, label: "Level 1", approverRole: "Reporting Manager" },
  { level: 2, label: "Level 2", approverRole: "Zonal Manager" },
  { level: 3, label: "Level 3", approverRole: "Business Head" },
];

const initialForm: SalesAgreementForm = {
  requestTitle: "",
  speakerDoctor: "",
  targetDoctor: "",
  requestDate: "",
  territory: "",
  notes: "",
};

const createExpenseRow = (): ExpenseDetail => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  expenseType: "",
  expenseDate: "",
  amount: "",
  remarks: "",
});

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatStatus = (status: SalesAgreementStatus) => status;

const getPendingStatus = (level: ApprovalLevel): SalesAgreementStatus => {
  if (level === 1) return "Pending Level 1";
  if (level === 2) return "Pending Level 2";
  return "Pending Level 3";
};

const isPendingStatus = (status: SalesAgreementStatus) =>
  status === "Pending Level 1" ||
  status === "Pending Level 2" ||
  status === "Pending Level 3";

const approvalActionLabel = (level: ApprovalLevel) => {
  if (level === 1) return "Approve to Level 2";
  if (level === 2) return "Approve to Level 3";
  return "Final Approve";
};

const normalizeRecord = (record: any): SalesAgreementRecord => {
  const status =
    record?.status === "Approved" || record?.status === "Rejected"
      ? record.status
      : record?.status === "Pending Level 2"
        ? "Pending Level 2"
        : record?.status === "Pending Level 3"
          ? "Pending Level 3"
          : "Pending Level 1";

  const level: ApprovalLevel =
    record?.currentLevel === 2
      ? 2
      : record?.currentLevel === 3
        ? 3
        : status === "Pending Level 2"
          ? 2
          : status === "Pending Level 3"
            ? 3
            : 1;

  return {
    id: String(record?.id ?? Date.now()),
    requestTitle: String(record?.requestTitle ?? ""),
    speakerDoctor: String(record?.speakerDoctor ?? ""),
    targetDoctor: String(record?.targetDoctor ?? ""),
    requestDate: String(record?.requestDate ?? ""),
    territory: String(record?.territory ?? ""),
    notes: String(record?.notes ?? ""),
    submittedBy: String(record?.submittedBy ?? "Admin User"),
    status,
    currentLevel: level,
    approvalTrail: Array.isArray(record?.approvalTrail)
      ? record.approvalTrail
      : [],
    expenseDetails: Array.isArray(record?.expenseDetails)
      ? record.expenseDetails
      : [],
    attachments: Array.isArray(record?.attachments) ? record.attachments : [],
    totalExpense: Number(record?.totalExpense ?? 0),
    reviewRemark: record?.reviewRemark,
    reviewedBy: record?.reviewedBy,
    reviewedAt: record?.reviewedAt,
    createdAt: String(record?.createdAt ?? new Date().toISOString()),
    updatedAt: String(record?.updatedAt ?? new Date().toISOString()),
  };
};

export default function SalesAgreement() {
  const [activeTab, setActiveTab] = useState("1");
  const [form, setForm] = useState<SalesAgreementForm>(initialForm);
  const [expenseRows, setExpenseRows] = useState<ExpenseDetail[]>([
    createExpenseRow(),
  ]);
  const [attachments, setAttachments] = useState<AttachmentDetail[]>([]);
  const [records, setRecords] = useState<SalesAgreementRecord[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<ReviewAction>("approve");
  const [selectedRecord, setSelectedRecord] =
    useState<SalesAgreementRecord | null>(null);
  const [reviewRemark, setReviewRemark] = useState("");

  const submittedBy =
    localStorage.getItem("userName") ||
    localStorage.getItem("fullName") ||
    "Admin User";

  useEffect(() => {
    const savedRecords = localStorage.getItem(STORAGE_KEY);
    if (!savedRecords) return;

    try {
      const parsed = JSON.parse(savedRecords);
      if (Array.isArray(parsed)) {
        setRecords(parsed.map((record) => normalizeRecord(record)));
      }
    } catch (error) {
      console.error("Failed to load sales agreement records", error);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  }, [records]);

  const summary = useMemo(() => {
    const pending = records.filter((record) => isPendingStatus(record.status))
      .length;
    const approved = records.filter(
      (record) => record.status === "Approved",
    ).length;
    const rejected = records.filter(
      (record) => record.status === "Rejected",
    ).length;

    return {
      total: records.length,
      pending,
      approved,
      rejected,
    };
  }, [records]);

  const pendingRecords = records.filter(
    (record) => isPendingStatus(record.status),
  );
  const historyRecords = [...records].sort(
    (a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!form.requestTitle.trim()) {
      toast.error("Please enter a request title.");
      return;
    }
    if (!form.speakerDoctor) {
      toast.error("Please select the speaker doctor.");
      return;
    }
    if (!form.targetDoctor) {
      toast.error("Please select the target doctor.");
      return;
    }
    if (form.speakerDoctor === form.targetDoctor) {
      toast.error("Speaker doctor and target doctor must be different.");
      return;
    }
    if (!form.requestDate) {
      toast.error("Please select the request date.");
      return;
    }
    if (!form.territory) {
      toast.error("Please select the territory.");
      return;
    }

    const validExpenseRows = expenseRows.filter(
      (row) =>
        row.expenseType.trim() ||
        row.expenseDate.trim() ||
        row.amount.trim() ||
        row.remarks.trim(),
    );

    if (validExpenseRows.length === 0) {
      toast.error("Please add at least one expense detail.");
      return;
    }

    const invalidExpenseRows = validExpenseRows.some(
      (row) =>
        !row.expenseType.trim() ||
        !row.expenseDate.trim() ||
        !row.amount.trim() ||
        Number.isNaN(Number(row.amount)) ||
        Number(row.amount) <= 0,
    );

    if (invalidExpenseRows) {
      toast.error(
        "Please complete each expense row with a type, date, and valid amount.",
      );
      return;
    }

    const totalExpense = validExpenseRows.reduce(
      (sum, row) => sum + Number(row.amount),
      0,
    );

    if (attachments.length > 0 && attachments.some((file) => !file.dataUrl)) {
      toast.error("One or more attachments could not be loaded.");
      return;
    }

    const now = new Date().toISOString();
    const nextRecord: SalesAgreementRecord = {
      id: `${Date.now()}`,
      requestTitle: form.requestTitle.trim(),
      speakerDoctor: form.speakerDoctor,
      targetDoctor: form.targetDoctor,
      requestDate: form.requestDate,
      territory: form.territory,
      notes: form.notes.trim(),
      submittedBy,
      status: "Pending Level 1",
      currentLevel: 1,
      approvalTrail: [],
      expenseDetails: validExpenseRows,
      attachments,
      totalExpense,
      createdAt: now,
      updatedAt: now,
    };

    setRecords((prev) => [nextRecord, ...prev]);
    setForm(initialForm);
    setExpenseRows([createExpenseRow()]);
    setAttachments([]);
    setActiveTab("2");
    toast.success("Sales agreement request sent for approval.");
  };

  const openReviewModal = (record: SalesAgreementRecord, action: ReviewAction) => {
    setSelectedRecord(record);
    setReviewAction(action);
    setReviewRemark("");
    setReviewModalOpen(true);
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
    setSelectedRecord(null);
    setReviewRemark("");
  };

  const confirmReview = () => {
    if (!selectedRecord) return;

    if (reviewAction === "reject" && !reviewRemark.trim()) {
      toast.error("Please add a rejection remark.");
      return;
    }

    const now = new Date().toISOString();
    const approver =
      localStorage.getItem("userName") ||
      localStorage.getItem("fullName") ||
      "Approver";

    setRecords((prev) =>
      prev.map((record) =>
        record.id === selectedRecord.id
          ? (() => {
              const trailEntry: ApprovalTrailEntry = {
                level: record.currentLevel,
                action: reviewAction === "approve" ? "approved" : "rejected",
                by: approver,
                remark:
                  reviewAction === "approve"
                    ? reviewRemark.trim() || `Approved at ${approvalHierarchy[record.currentLevel - 1].label}.`
                    : reviewRemark.trim(),
                at: now,
              };

              if (reviewAction === "approve") {
                const nextLevel = record.currentLevel < 3 ? (record.currentLevel + 1) as ApprovalLevel : 3;
                const isFinalLevel = record.currentLevel === 3;
                return {
                  ...record,
                  status: isFinalLevel
                    ? "Approved"
                    : getPendingStatus(nextLevel),
                  currentLevel: isFinalLevel ? 3 : nextLevel,
                  approvalTrail: [...record.approvalTrail, trailEntry],
                  reviewRemark: trailEntry.remark,
                  reviewedBy: approver,
                  reviewedAt: now,
                  updatedAt: now,
                };
              }

              return {
                ...record,
                status: "Rejected",
                approvalTrail: [...record.approvalTrail, trailEntry],
                reviewRemark: trailEntry.remark,
                reviewedBy: approver,
                reviewedAt: now,
                updatedAt: now,
              };
            })()
          : record,
      ),
    );

    toast.success(
      reviewAction === "approve"
        ? selectedRecord.currentLevel < 3
          ? `Approved at level ${selectedRecord.currentLevel}. Sent to level ${selectedRecord.currentLevel + 1}.`
          : "Sales agreement fully approved."
        : "Sales agreement rejected.",
    );
    closeReviewModal();
  };

  const statusColor = (status: SalesAgreementStatus) => {
    if (status === "Approved") return "success";
    if (status === "Rejected") return "danger";
    return "warning";
  };

  const doctorLabel = (doctorId: string) =>
    doctorOptions.find((doctor) => doctor.id === doctorId)?.name ?? doctorId;

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    if (attachments.length + files.length > MAX_ATTACHMENT_COUNT) {
      toast.error(`You can attach up to ${MAX_ATTACHMENT_COUNT} files.`);
      event.target.value = "";
      return;
    }

    const oversizedFile = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversizedFile) {
      toast.error(
        `${oversizedFile.name} is too large. Maximum allowed size is 5 MB per file.`,
      );
      event.target.value = "";
      return;
    }

    try {
      const nextAttachments = await Promise.all(
        files.map(async (file) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          type: file.type || "application/octet-stream",
          size: file.size,
          dataUrl: await readFileAsDataUrl(file),
        })),
      );
      setAttachments((prev) => [...prev, ...nextAttachments]);
    } catch (error) {
      console.error("Failed to read attachment", error);
      toast.error("Could not load one or more attachments.");
    } finally {
      event.target.value = "";
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  };

  const openAttachment = (attachment: AttachmentDetail) => {
    const blobUrl = attachment.dataUrl || "";
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  const addExpenseRow = () => {
    setExpenseRows((prev) => [...prev, createExpenseRow()]);
  };

  const updateExpenseRow = (
    id: string,
    field: keyof ExpenseDetail,
    value: string,
  ) => {
    setExpenseRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const removeExpenseRow = (id: string) => {
    setExpenseRows((prev) =>
      prev.length > 1 ? prev.filter((row) => row.id !== id) : prev,
    );
  };

  const expenseTotal = expenseRows.reduce(
    (sum, row) => sum + (Number(row.amount) || 0),
    0,
  );

  return (
    <div className="page-body p-2">
      <Col xxl={12}>
        <Card>
          <CardHeaderSpan
            headingClassName="py-2 px-2"
            back={true}
            heading={"Sales Agreement"}
            span={"Create a doctor-to-doctor request and move it through approval"}
          />
          <CardBody className="pt-0">
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Card className="h-100 shadow-sm">
                  <CardBody>
                    <div className="text-muted">Total Requests</div>
                    <h4 className="mb-0">{summary.total}</h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 shadow-sm">
                  <CardBody>
                    <div className="text-muted">Pending Approval</div>
                    <h4 className="mb-0">{summary.pending}</h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 shadow-sm">
                  <CardBody>
                    <div className="text-muted">Approved</div>
                    <h4 className="mb-0">{summary.approved}</h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="h-100 shadow-sm">
                  <CardBody>
                    <div className="text-muted">Rejected</div>
                    <h4 className="mb-0">{summary.rejected}</h4>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Card className="border shadow-sm mb-3">
              <CardBody className="py-3">
                <div className="fw-semibold mb-2">Workflow</div>
                <div className="text-muted">
                  1. Create a sales agreement request.
                  {" "}
                  2. Choose the speaker doctor and the target doctor.
                  {" "}
                  3. Send it to approval.
                  {" "}
                  4. Approve or reject it from the pending queue.
                </div>
                <Row className="g-2 mt-3">
                  {approvalHierarchy.map((item) => (
                    <Col md={4} key={item.level}>
                      <div className="border rounded p-2 h-100">
                        <div className="fw-semibold">{item.label}</div>
                        <div className="text-muted small">{item.approverRole}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            <Nav tabs className="border-tab nav-primary mb-0">
              <NavItem>
                <NavLink
                  href={Href}
                  className={`nav-border txt-primary tab-primary ${activeTab === "1" ? "active" : ""}`}
                  onClick={() => setActiveTab("1")}
                >
                  Create Request
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href={Href}
                  className={`nav-border txt-primary tab-primary ${activeTab === "2" ? "active" : ""}`}
                  onClick={() => setActiveTab("2")}
                >
                  Pending Approval
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href={Href}
                  className={`nav-border txt-primary tab-primary ${activeTab === "3" ? "active" : ""}`}
                  onClick={() => setActiveTab("3")}
                >
                  History
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="1" className={activeTab === "1" ? "show" : ""}>
                <div className="pt-4">
                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Request Title</label>
                          <Input
                            value={form.requestTitle}
                            onChange={(e) =>
                              setForm({ ...form, requestTitle: e.target.value })
                            }
                            placeholder="Doctor-to-doctor speaking request"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Request Date</label>
                          <Input
                            type="date"
                            value={form.requestDate}
                            onChange={(e) =>
                              setForm({ ...form, requestDate: e.target.value })
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Speaker Doctor</label>
                          <Input
                            type="select"
                            value={form.speakerDoctor}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                speakerDoctor: e.target.value,
                              })
                            }
                          >
                            <option value="">Select speaker doctor</option>
                            {doctorOptions.map((doctor) => (
                              <option key={doctor.id} value={doctor.id}>
                                {doctor.name} - {doctor.specialty}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Target Doctor</label>
                          <Input
                            type="select"
                            value={form.targetDoctor}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                targetDoctor: e.target.value,
                              })
                            }
                          >
                            <option value="">Select target doctor</option>
                            {doctorOptions.map((doctor) => (
                              <option key={doctor.id} value={doctor.id}>
                                {doctor.name} - {doctor.specialty}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Territory / HQ</label>
                          <Input
                            type="select"
                            value={form.territory}
                            onChange={(e) =>
                              setForm({ ...form, territory: e.target.value })
                            }
                          >
                            <option value="">Select territory</option>
                            {territoryOptions.map((territory) => (
                              <option key={territory} value={territory}>
                                {territory}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <label className="form-label">Created By</label>
                          <Input value={submittedBy} disabled />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <label className="form-label">Notes</label>
                          <Input
                            type="textarea"
                            rows={4}
                            value={form.notes}
                            onChange={(e) =>
                              setForm({ ...form, notes: e.target.value })
                            }
                            placeholder="Add the speaking agenda, expected outcome, and any approval context."
                          />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">Expense Details</label>
                          <Button color="link" type="button" onClick={addExpenseRow}>
                            + Add Expense Row
                          </Button>
                        </div>
                        <div className="table-responsive border rounded">
                          <Table className="mb-0 align-middle">
                            <thead className="table-light">
                              <tr>
                                <th>Expense Type</th>
                                <th>Expense Date</th>
                                <th>Amount</th>
                                <th>Remarks</th>
                                <th className="text-end">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expenseRows.map((row, index) => (
                                <tr key={row.id}>
                                  <td>
                                    <Input
                                      value={row.expenseType}
                                      onChange={(e) =>
                                        updateExpenseRow(
                                          row.id,
                                          "expenseType",
                                          e.target.value,
                                        )
                                      }
                                      placeholder={`Expense type ${index + 1}`}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="date"
                                      value={row.expenseDate}
                                      onChange={(e) =>
                                        updateExpenseRow(
                                          row.id,
                                          "expenseDate",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={row.amount}
                                      onChange={(e) =>
                                        updateExpenseRow(
                                          row.id,
                                          "amount",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="0.00"
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      value={row.remarks}
                                      onChange={(e) =>
                                        updateExpenseRow(
                                          row.id,
                                          "remarks",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Optional remarks"
                                    />
                                  </td>
                                  <td className="text-end">
                                    <Button
                                      color="danger"
                                      type="button"
                                      size="sm"
                                      onClick={() => removeExpenseRow(row.id)}
                                      disabled={expenseRows.length === 1}
                                    >
                                      Remove
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        <div className="text-end mt-2 fw-semibold">
                          Expense Total: ₹{expenseTotal.toFixed(2)}
                        </div>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label mb-0">Attachments</label>
                            <span className="text-muted small">
                              Optional documents, images, or PDFs
                            </span>
                          </div>
                          <Input
                            type="file"
                            multiple
                            onChange={handleAttachmentChange}
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*,application/pdf"
                          />
                        </FormGroup>
                        {attachments.length > 0 && (
                          <div className="border rounded p-3">
                            <div className="fw-semibold mb-2">Selected Files</div>
                            <div className="d-flex flex-column gap-2">
                              {attachments.map((attachment) => (
                                <div
                                  key={attachment.id}
                                  className="d-flex justify-content-between align-items-center border rounded px-3 py-2"
                                >
                                  <div>
                                    <div className="fw-semibold">{attachment.name}</div>
                                    <div className="text-muted small">
                                      {formatFileSize(attachment.size)}{" "}
                                      {attachment.type ? `• ${attachment.type}` : ""}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="info"
                                      outline
                                      size="sm"
                                      type="button"
                                      onClick={() => openAttachment(attachment)}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      color="danger"
                                      outline
                                      size="sm"
                                      type="button"
                                      onClick={() => removeAttachment(attachment.id)}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <Button
                        color="light"
                        type="button"
                        onClick={() => {
                          setForm(initialForm);
                          setExpenseRows([createExpenseRow()]);
                          setAttachments([]);
                        }}
                      >
                        Reset
                      </Button>
                      <Button color="primary" type="submit">
                        Submit for Approval
                      </Button>
                    </div>
                  </Form>
                </div>
              </TabPane>

              <TabPane tabId="2" className={activeTab === "2" ? "show" : ""}>
                <div className="pt-4">
                  <Card className="shadow-sm">
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <Table className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Request</th>
                              <th>Speaker Doctor</th>
                              <th>Target Doctor</th>
                              <th>Territory</th>
                              <th>Expense Total</th>
                              <th>Attachments</th>
                              <th>Current Level</th>
                              <th>Request Date</th>
                              <th>Status</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingRecords.length === 0 ? (
                              <tr>
                                <td colSpan={10} className="text-center py-4">
                                  No pending sales agreement requests.
                                </td>
                              </tr>
                            ) : (
                              pendingRecords.map((record) => (
                                <tr key={record.id}>
                                  <td>
                                    <div className="fw-semibold">
                                      {record.requestTitle}
                                    </div>
                                    <div className="text-muted small">
                                      Created by {record.submittedBy}
                                    </div>
                                  </td>
                                  <td>{doctorLabel(record.speakerDoctor)}</td>
                                  <td>{doctorLabel(record.targetDoctor)}</td>
                                  <td>{record.territory}</td>
                                  <td>₹{record.totalExpense.toFixed(2)}</td>
                                  <td>
                                    <Badge color="secondary">
                                      {record.attachments.length} file
                                      {record.attachments.length === 1 ? "" : "s"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge color="info">
                                      {approvalHierarchy.find(
                                        (item) => item.level === record.currentLevel,
                                      )?.label ?? `Level ${record.currentLevel}`}
                                    </Badge>
                                  </td>
                                  <td>{formatDate(record.requestDate)}</td>
                                  <td>
                                    <Badge color={statusColor(record.status)}>
                                      {formatStatus(record.status)}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() =>
                                          openReviewModal(record, "approve")
                                        }
                                      >
                                        {approvalActionLabel(record.currentLevel)}
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() =>
                                          openReviewModal(record, "reject")
                                        }
                                      >
                                        Reject
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </TabPane>

              <TabPane tabId="3" className={activeTab === "3" ? "show" : ""}>
                <div className="pt-4">
                  <Card className="shadow-sm">
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <Table className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Request</th>
                              <th>Doctors</th>
                              <th>Territory</th>
                              <th>Expense Total</th>
                              <th>Attachments</th>
                              <th>Submitted</th>
                              <th>Stage</th>
                              <th>Status</th>
                              <th>Review Remark</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historyRecords.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="text-center py-4">
                                  No sales agreement history yet.
                                </td>
                              </tr>
                            ) : (
                              historyRecords.map((record) => (
                                <tr key={record.id}>
                                  <td>
                                    <div className="fw-semibold">
                                      {record.requestTitle}
                                    </div>
                                    <div className="text-muted small">
                                      Request date {formatDate(record.requestDate)}
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <span className="text-muted">Speaker: </span>
                                      {doctorLabel(record.speakerDoctor)}
                                    </div>
                                    <div>
                                      <span className="text-muted">Target: </span>
                                      {doctorLabel(record.targetDoctor)}
                                    </div>
                                  </td>
                                  <td>{record.territory}</td>
                                  <td>₹{record.totalExpense.toFixed(2)}</td>
                                  <td>
                                    <Badge color="secondary">
                                      {record.attachments.length} file
                                      {record.attachments.length === 1 ? "" : "s"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div>{formatDate(record.createdAt)}</div>
                                    <div className="text-muted small">
                                      by {record.submittedBy}
                                    </div>
                                  </td>
                                  <td>
                                    {record.status === "Approved" ||
                                    record.status === "Rejected"
                                      ? record.status
                                      : approvalHierarchy.find(
                                          (item) => item.level === record.currentLevel,
                                        )?.label ?? `Level ${record.currentLevel}`}
                                  </td>
                                  <td>
                                    <Badge color={statusColor(record.status)}>
                                      {formatStatus(record.status)}
                                    </Badge>
                                  </td>
                                  <td className="text-muted">
                                    {record.approvalTrail.length > 0
                                      ? record.approvalTrail
                                          .map(
                                            (entry) =>
                                              `${approvalHierarchy.find((item) => item.level === entry.level)?.label ?? `Level ${entry.level}`}: ${entry.action} (${entry.remark || "-"})`,
                                          )
                                          .join(" | ")
                                      : record.reviewRemark || "-"}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Col>

      <Modal isOpen={reviewModalOpen} toggle={closeReviewModal} centered>
        <ModalHeader toggle={closeReviewModal}>
          {reviewAction === "approve"
            ? "Approve Sales Agreement"
            : "Reject Sales Agreement"}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {reviewAction === "approve"
              ? "Are you sure you want to approve this request?"
              : "Please share the rejection remark before closing the request."}
          </p>
          {selectedRecord && (
            <div className="mb-3">
              <div className="fw-semibold">{selectedRecord.requestTitle}</div>
              <div className="text-muted small">
                {doctorLabel(selectedRecord.speakerDoctor)} to{" "}
                {doctorLabel(selectedRecord.targetDoctor)}
              </div>
              <div className="text-muted small">
                Attachments: {selectedRecord.attachments.length}
              </div>
              {selectedRecord.attachments.length > 0 && (
                <div className="d-flex flex-column gap-2 mt-2">
                  {selectedRecord.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="d-flex justify-content-between align-items-center border rounded px-2 py-1"
                    >
                      <span className="small">{attachment.name}</span>
                      <Button
                        color="info"
                        outline
                        size="sm"
                        type="button"
                        onClick={() => openAttachment(attachment)}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <FormGroup>
            <label className="form-label">
              {reviewAction === "approve" ? "Optional remark" : "Rejection remark"}
            </label>
            <Input
              type="textarea"
              rows={4}
              value={reviewRemark}
              onChange={(e) => setReviewRemark(e.target.value)}
              placeholder={
                reviewAction === "approve"
                  ? "Add a short approval note if needed."
                  : "Explain why the request is being rejected."
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeReviewModal}>
            Cancel
          </Button>
          <Button
            color={reviewAction === "approve" ? "success" : "danger"}
            onClick={confirmReview}
          >
            {reviewAction === "approve" ? "Approve" : "Reject"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
